import posthog from "posthog-js";

/**
 * Calculates the number of days between today and a future date
 * @param {Date|string} futureDate - The future date
 * @returns {number} Number of days until the future date
 */
export const calculateDaysUntil = (futureDate) => {
  if (!futureDate) return null;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const future = new Date(futureDate);
  future.setHours(0, 0, 0, 0);
  
  const diffTime = future - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays > 0 ? diffDays : 0;
};

/**
 * Determines the payment schedule type based on payment data
 * @param {Object} paymentData - Payment data object
 * @returns {string} Payment schedule type
 */
export const getPaymentScheduleType = (paymentData) => {
  if (!paymentData) return "immediate";
  
  if (paymentData.scheduled_date || paymentData.payment_date) {
    const daysUntil = calculateDaysUntil(paymentData.scheduled_date || paymentData.payment_date);
    if (daysUntil > 0) return "future";
  }
  
  if (paymentData.is_recurring || paymentData.payment_plan) {
    return "recurring";
  }
  
  return "immediate";
};

/**
 * Track modal opened event
 * @param {string} modalType - Type of modal being opened
 * @param {Object} props - Additional properties to track
 */
export const trackModalOpened = (modalType, props = {}) => {
  if (!posthog || !modalType) return;
  
  const safeProps = {
    modal_type: modalType,
    timestamp: new Date().toISOString(),
  };
  
  // Add payment/offer specific props if applicable
  if (props.amount !== undefined) {
    safeProps.amount = props.amount;
  }
  
  if (props.payment_type) {
    safeProps.payment_type = props.payment_type;
  }
  
  posthog.capture("modal_opened", safeProps);
};

/**
 * Track modal shown event (when actually rendered)
 * @param {string} modalType - Type of modal being shown
 */
export const trackModalShown = (modalType) => {
  if (!posthog || !modalType) return;
  
  posthog.capture("modal_shown", {
    modal_type: modalType,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track modal closed event
 * @param {string} modalType - Type of modal being closed
 * @param {number} duration - How long the modal was open in milliseconds
 * @param {string} closeMethod - How the modal was closed
 */
export const trackModalClosed = (modalType, duration, closeMethod = "unknown") => {
  if (!posthog || !modalType) return;
  
  posthog.capture("modal_closed", {
    modal_type: modalType,
    duration_ms: duration,
    duration_seconds: Math.round(duration / 1000),
    close_method: closeMethod,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track offer submission
 * @param {Object} offerData - Offer data
 */
export const trackOfferSubmitted = (offerData) => {
  if (!posthog) return;
  
  const eventData = {
    amount: offerData.amount,
    outcome: offerData.outcome || "submitted",
    payment_type: "settlement_offer",
    timestamp: new Date().toISOString(),
  };
  
  if (offerData.payment_date) {
    const daysUntil = calculateDaysUntil(offerData.payment_date);
    if (daysUntil !== null) {
      eventData.days_until_payment = daysUntil;
      eventData.is_scheduled = daysUntil > 0;
      eventData.payment_schedule_type = daysUntil > 0 ? "future" : "immediate";
    }
  }
  
  posthog.capture("offer_submitted", eventData);
};

/**
 * Track payment submission
 * @param {Object} paymentData - Payment data
 */
export const trackPaymentSubmitted = (paymentData) => {
  if (!posthog) return;
  
  const eventData = {
    amount: paymentData.amount,
    outcome: paymentData.outcome || "submitted",
    payment_type: paymentData.payment_type || "single",
    timestamp: new Date().toISOString(),
  };
  
  const scheduleType = getPaymentScheduleType(paymentData);
  eventData.payment_schedule_type = scheduleType;
  eventData.is_scheduled = scheduleType !== "immediate";
  
  if (paymentData.scheduled_date || paymentData.payment_date) {
    const daysUntil = calculateDaysUntil(paymentData.scheduled_date || paymentData.payment_date);
    if (daysUntil !== null) {
      eventData.days_until_payment = daysUntil;
    }
  }

  if (paymentData.plan_months) {
    eventData.plan_months = paymentData.plan_months;
    eventData.monthly_payment = paymentData.monthly_payment;
  }

  posthog.capture("payment_submitted", eventData);
};

/**
 * Track settlement response
 * @param {string} outcome - "accepted" or "declined"
 * @param {Object} settlementData - Settlement data
 */
export const trackSettlementResponse = (outcome, settlementData = {}) => {
  if (!posthog) return;
  
  const eventData = {
    outcome,
    timestamp: new Date().toISOString(),
  };
  
  if (settlementData.amount) {
    eventData.amount = settlementData.amount;
  }
  
  if (settlementData.payment_date) {
    const daysUntil = calculateDaysUntil(settlementData.payment_date);
    if (daysUntil !== null) {
      eventData.days_until_payment = daysUntil;
      eventData.is_scheduled = daysUntil > 0;
    }
  }
  
  posthog.capture("settlement_response", eventData);
};

/**
 * Track payment method added
 * @param {string} methodType - "credit_card" or "bank_account"
 * @param {boolean} success - Whether the addition was successful
 */
export const trackPaymentMethodAdded = (methodType, success = true) => {
  if (!posthog) return;
  
  posthog.capture("payment_method_added", {
    method_type: methodType,
    success,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track payment method deleted
 * @param {string} methodType - Type of payment method deleted
 */
export const trackPaymentMethodDeleted = (methodType) => {
  if (!posthog) return;
  
  posthog.capture("payment_method_deleted", {
    method_type: methodType,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track scheduled payment cancelled
 * @param {number} amount - Payment amount
 * @param {string} scheduledDate - Scheduled payment date
 */
export const trackScheduledPaymentCancelled = (amount, scheduledDate) => {
  if (!posthog) return;
  
  const eventData = {
    timestamp: new Date().toISOString(),
  };
  
  if (amount !== undefined) {
    eventData.amount = amount;
  }
  
  if (scheduledDate) {
    const daysUntil = calculateDaysUntil(scheduledDate);
    if (daysUntil !== null) {
      eventData.days_until_payment = daysUntil;
    }
  }
  
  posthog.capture("scheduled_payment_cancelled", eventData);
};

/**
 * Track questionnaire submission
 * @param {string} type - "financial" or "sentiment"
 * @param {number} completionTime - Time taken to complete in seconds
 */
export const trackQuestionnaireSubmitted = (type, completionTime) => {
  if (!posthog) return;
  
  const eventName = type === "financial" 
    ? "financial_questionnaire_submitted" 
    : "sentiment_questionnaire_submitted";
  
  posthog.capture(eventName, {
    completion_time_seconds: completionTime,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track sentiment questionnaire skipped
 */
export const trackSentimentQuestionnaireSkipped = () => {
  if (!posthog) return;
  
  posthog.capture("sentiment_questionnaire_skipped", {
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track chat link clicked
 * @param {string} source - Where the chat was opened from
 * @param {string} wellnessArea - If from wellness coach, which area
 */
export const trackChatLinkClicked = (source, wellnessArea = null) => {
  if (!posthog) return;
  
  const eventData = {
    source,
    timestamp: new Date().toISOString(),
  };
  
  if (wellnessArea) {
    eventData.wellness_area = wellnessArea;
  }
  
  posthog.capture("chat_link_clicked", eventData);
};

/**
 * Track profile updated
 * @param {Array} fieldsChanged - Array of field names that were changed
 */
export const trackProfileUpdated = (fieldsChanged = []) => {
  if (!posthog) return;
  
  posthog.capture("profile_updated", {
    fields_changed: fieldsChanged,
    field_count: fieldsChanged.length,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track opt-in preference changed
 * @param {string} type - "email" or "sms"
 * @param {boolean} newValue - New opt-in value
 */
export const trackOptinPreferenceChanged = (type, newValue) => {
  if (!posthog) return;
  
  posthog.capture("optin_preference_changed", {
    type,
    new_value: newValue,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track profile picture uploaded
 */
export const trackProfilePictureUploaded = () => {
  if (!posthog) return;
  
  posthog.capture("profile_picture_uploaded", {
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track quick access login success
 * @param {string} referenceNumber - The user's reference number
 */
export const trackQuickAccessLoginSuccess = (referenceNumber) => {
  if (!posthog) return;
  
  posthog.capture("quick_access_login_success", {
    reference_number: referenceNumber,
    login_method: "quick_access",
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track quick access login failed
 * @param {Object} errorDetails - Details about the failure
 */
export const trackQuickAccessLoginFailed = (errorDetails = {}) => {
  if (!posthog) return;
  
  const eventData = {
    login_method: "quick_access",
    timestamp: new Date().toISOString(),
  };
  
  if (errorDetails.errorType) {
    eventData.error_type = errorDetails.errorType;
  }
  
  if (errorDetails.fieldsWithErrors) {
    eventData.fields_with_errors = errorDetails.fieldsWithErrors;
  }
  
  if (errorDetails.errorMessage) {
    eventData.error_message = errorDetails.errorMessage;
  }
  
  posthog.capture("quick_access_login_failed", eventData);
};